/** Warrants: search the active file, request a warrant, the warrants office's entry and request queue, and My warrants. */
import { useEffect, useState } from 'react';
import {
  US_STATES, WARRANT_CAUTION_LABEL, WARRANT_CAUTIONS, WARRANT_KIND_LABEL, WARRANT_KINDS, WARRANT_REQUEST_KINDS, WARRANT_REQUEST_STATUS_LABEL, WARRANT_STATUS_LABEL,
  type WarrantKind, type WarrantRequestStatus, type WarrantStatus,
} from '@civilos/domain';
import { api } from '../shared/api.ts';
import { chargeWords, StatutePicker } from '../shared/StatutePicker.tsx';
import { LicenseScanButton } from '../shared/LicenseScan.tsx';
import { useAuth } from '../shared/auth.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, KPI, when } from './common.tsx';

type Subject = Record<string, string>;
type Charge = { offense: string; statute: string; counts: number; bond: string; cdr?: string | null };
const emptySubject = (): Subject => ({ last: '', first: '', middle: '', suffix: '', dob: '', sex: '', race: '', height: '', weight: '', hair: '', eyes: '', address: '', city: '', state: 'SC', zip: '', dl: '', dlState: 'SC', aliases: '', description: '' });
const RTONE: Record<string, 'ok' | 'warn' | 'bad' | 'info' | 'neutral'> = { submitted: 'info', in_review: 'info', needs_info: 'warn', sent_to_judge: 'info', issued: 'ok', denied: 'bad', withdrawn: 'neutral' };
const WTONE: Record<string, 'ok' | 'warn' | 'bad' | 'info' | 'neutral'> = { active: 'bad', served: 'ok', recalled: 'neutral', cancelled: 'neutral' };
const age = (dob?: string | null) => (dob ? Math.floor((Date.now() - Date.parse(`${dob}T12:00:00`)) / 31_557_600_000) : null);
const subjectLine = (s: any) => [s?.dob && `DOB ${day(s.dob)} (${age(s.dob)})`, s?.sex, s?.race, s?.height, s?.weight && `${s.weight} lb`].filter(Boolean).join(' · ');
const clean = (o: Record<string, any>) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, typeof v === 'string' && !v.trim() ? null : v]));

/** Tabs across the warrant pages (the office sees Entry and Requests). */
function WarrantTabs() {
  const { can } = useAuth();
  const { path, navigate } = useRouter();
  const p = path.replace(/^\/field/, '').split('?')[0]!;
  const tabs: [string, string, boolean][] = [
    ['/warrants', 'Active', can('warrants.view') || can('warrants.enter')],
    ['/warrants/request', 'Request', can('warrants.request')],
    ['/warrants/mine', 'My warrants', can('warrants.request') || can('warrants.view')],
    ['/warrants/queue', 'Requests', can('warrants.enter') || can('warrants.sign')],
    ['/warrants/entry', 'Entry', can('warrants.enter')],
  ];
  return (
    <div className="tabs" role="tablist" aria-label="Warrants">
      {tabs
        .filter((t) => t[2])
        .map(([to, l]) => (
          <button key={to} role="tab" aria-selected={p === to} className={p === to ? 'active' : ''} onClick={() => navigate(at(to))}>
            {l}
          </button>
        ))}
    </div>
  );
}

function Cautions({ list }: { list: string[] }) {
  return list?.length ? (
    <span className="w-cautions">
      {list.map((c) => (
        <span key={c} className="w-caution">
          ⚠ {WARRANT_CAUTION_LABEL[c as keyof typeof WARRANT_CAUTION_LABEL] ?? c}
        </span>
      ))}
    </span>
  ) : null;
}

// ─── Active warrants ──────────────────────────────────────────────────────────

export function WarrantsActive() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('active');
  const [kind, setKind] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const r = useLoad(() => api.get<any>(`/api/warrants?status=${status}&kind=${kind}${query ? `&q=${encodeURIComponent(query)}` : ''}`), [query, status, kind]);
  const list: any[] = r.data?.warrants ?? [];
  return (
    <div className="ops stack">
      <PageHeader crumb="Warrants" title="Active warrants" sub="Search by name, date of birth, warrant #, case #, address or license number." />
      <WarrantTabs />
      {r.data && (
        <div className="ops-kpis">
          <KPI n={r.data.counts.active} label="Active warrants" tone={r.data.counts.active ? 'bad' : undefined} on={status === 'active'} onClick={() => setStatus('active')} />
          <KPI n={r.data.counts.served30} label="Served, last 30 days" tone="ok" on={status === 'served'} onClick={() => setStatus('served')} />
        </div>
      )}
      <div className="filterbar">
        <input type="search" className="rp-search w-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Smith, John · 04/01/1988 · 2026A12… · 12 Elm St" aria-label="Search warrants" autoFocus />
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
          <option value="">All kinds</option>
          {WARRANT_KINDS.map((k) => (
            <option key={k} value={k}>
              {WARRANT_KIND_LABEL[k]}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="active">Active</option>
          <option value="served">Served</option>
          <option value="recalled">Recalled</option>
          <option value="cancelled">Cancelled</option>
          <option value="all">All</option>
        </select>
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !list.length ? (
        <EmptyState icon="search" title={query ? 'No warrants match' : 'No warrants here'}>
          {query && status === 'active' ? 'No active warrant for that search.' : undefined}
        </EmptyState>
      ) : (
        <div className="w-list">
          {list.map((w) => (
            <button key={w.id} type="button" className={`card w-row s-${w.status}`} onClick={() => navigate(at(`/warrants/${w.id}`))}>
              <div className="w-main">
                <strong className="w-name">{w.subject_name}</strong>
                <span className="small">{subjectLine(w.subject)}</span>
                {w.subject?.address && <span className="small muted">{[w.subject.address, w.subject.city].filter(Boolean).join(', ')}</span>}
                <span className="small">{(w.charges ?? []).map((c: any) => c.offense).join(' · ') || WARRANT_KIND_LABEL[w.kind as WarrantKind]}</span>
                <Cautions list={w.cautions} />
              </div>
              <div className="w-side">
                <Badge tone={WTONE[w.status]} dot>
                  {WARRANT_STATUS_LABEL[w.status as WarrantStatus]}
                </Badge>
                <span className="mono small">{w.warrant_number}</span>
                <span className="small muted">{WARRANT_KIND_LABEL[w.kind as WarrantKind]}</span>
                {w.issued_on && <span className="small muted">Issued {day(w.issued_on)}</span>}
                {w.assigned_name && <span className="small">→ {w.assigned_name}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── One warrant ──────────────────────────────────────────────────────────────

export function WarrantDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/warrants/${id}`), [id]);
  const staff = useLoad(() => api.get<any>('/api/mail/people').then((x) => x.people as any[]).catch(() => []), []);
  const [doing, setDoing] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [location, setLocation] = useState('');
  const [assignee, setAssignee] = useState('');
  const [editing, setEditing] = useState(false);
  const a = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const w = r.data.warrant;
  const s = w.subject ?? {};
  const go = () =>
    a.run(async () => {
      await api.post(`/api/warrants/${id}/act`, { action: doing, note: note || undefined, location: location || undefined, assignedTo: doing === 'assign' ? assignee || null : undefined });
      setDoing(null);
      setNote('');
      setLocation('');
      r.reload();
    });
  const ACT: [string, string, boolean][] = [
    ['attempt', 'Log an attempt', r.data.canServe && w.status === 'active'],
    ['served', 'Served', r.data.canServe && w.status === 'active'],
    ['note', 'Add a note', true],
    ['assign', 'Assign', r.data.canEnter && w.status === 'active'],
    ['recall', 'Recalled', r.data.canEnter && w.status === 'active'],
    ['cancel', 'Cancel', r.data.canEnter && w.status === 'active'],
    ['reactivate', 'Make active again', r.data.canEnter && w.status !== 'active'],
  ];
  if (editing) return <WarrantForm existing={w} onDone={() => (setEditing(false), r.reload())} />;
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<Link to={at('/warrants')}>Warrants</Link>}
        title={w.subject_name}
        sub={`${WARRANT_KIND_LABEL[w.kind as WarrantKind]} ${w.warrant_number}`}
        actions={
          r.data.canEnter && (
            <button className="btn" onClick={() => setEditing(true)}>
              Correct entry
            </button>
          )
        }
      />
      <div className={`alert ${w.status === 'active' ? 'bad' : w.status === 'served' ? 'ok' : 'info'} w-banner`}>
        <strong>{WARRANT_STATUS_LABEL[w.status as WarrantStatus].toUpperCase()}</strong>
        {w.status === 'served' && ` — served ${when(w.served_at)} by ${w.served_by_name}${w.served_location ? ` at ${w.served_location}` : ''}`}
        {w.closed_reason && ` — ${w.closed_reason}`}
        <Cautions list={w.cautions} />
      </div>
      <div className="grid-2">
        <Card title="Subject">
          <dl className="kv">
            <dt>Name</dt>
            <dd>
              <strong>{w.subject_name}</strong>
              {s.aliases && <div className="small muted">AKA {s.aliases}</div>}
            </dd>
            <dt>DOB</dt>
            <dd>{s.dob ? `${day(s.dob)} (age ${age(s.dob)})` : '—'}</dd>
            <dt>Description</dt>
            <dd>{[s.sex, s.race, s.height, s.weight && `${s.weight} lb`, s.hair && `hair ${s.hair}`, s.eyes && `eyes ${s.eyes}`].filter(Boolean).join(' · ') || '—'}</dd>
            <dt>Address</dt>
            <dd>{[s.address, s.city, s.state, s.zip].filter(Boolean).join(', ') || '—'}</dd>
            <dt>License</dt>
            <dd className="mono">{s.dl ? `${s.dl} (${s.dlState ?? ''})` : '—'}</dd>
            {s.description && (
              <>
                <dt>Other</dt>
                <dd className="pre">{s.description}</dd>
              </>
            )}
          </dl>
        </Card>
        <Card title="Warrant">
          <dl className="kv">
            <dt>Warrant #</dt>
            <dd className="mono">{w.warrant_number}</dd>
            <dt>Court</dt>
            <dd>{[w.court_name, w.judge_name].filter(Boolean).join(' · ') || '—'}</dd>
            <dt>Issued</dt>
            <dd>{day(w.issued_on)}</dd>
            <dt>Bond</dt>
            <dd>{w.bond ?? '—'}</dd>
            <dt>NCIC</dt>
            <dd className="mono">{w.ncic_number ?? <span className="muted">not entered</span>}</dd>
            <dt>Case #</dt>
            <dd>{w.incident_id ? <Link to={at(`/cases/${w.incident_id}`)}>{w.case_number}</Link> : w.case_number ?? '—'}</dd>
            <dt>Assigned</dt>
            <dd>{w.assigned_name ?? '—'}</dd>
            <dt>Entered by</dt>
            <dd>
              {w.entered_by_name}, {day(w.created_at)}
            </dd>
          </dl>
        </Card>
      </div>
      <Card title="Charges">
        {w.charges?.length ? (
          <table className="table">
            <thead>
              <tr>
                <th>Offense</th>
                <th>Statute</th>
                <th>Counts</th>
                <th>Bond</th>
              </tr>
            </thead>
            <tbody>
              {w.charges.map((c: any, i: number) => (
                <tr key={i}>
                  <td>{c.offense}</td>
                  <td className="mono">{c.statute ?? '—'}</td>
                  <td>{c.counts ?? 1}</td>
                  <td>{c.bond ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">None listed.</p>
        )}
        {w.notes && <p className="pre small">{w.notes}</p>}
      </Card>
      <Card title="Follow-up">
        <div className="row gap-sm wrap">
          {ACT.filter((x) => x[2]).map(([k, l]) => (
            <button key={k} className={`btn ${doing === k ? 'primary' : k === 'served' ? 'primary' : ''}`} onClick={() => (setDoing(k), setNote(''), setLocation(''))}>
              {l}
            </button>
          ))}
        </div>
        {doing && (
          <div className="stack sm" style={{ marginTop: 10 }}>
            {doing === 'assign' ? (
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)} aria-label="Assign to">
                <option value="">Nobody</option>
                {(staff.data ?? []).map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            ) : null}
            {(doing === 'attempt' || doing === 'served') && <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={doing === 'served' ? 'Where served / booked' : 'Where you tried'} aria-label="Location" />}
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={doing === 'attempt' ? 'What happened (no answer, moved, info from neighbor…)' : doing === 'recall' || doing === 'cancel' ? 'Why (recalled by the court, paid, duplicate…)' : 'Note'} aria-label="Note" />
            <div className="row gap-sm">
              <button className="btn primary" disabled={a.busy} onClick={go}>
                Save
              </button>
              <button className="btn ghost" onClick={() => setDoing(null)}>
                Cancel
              </button>
            </div>
            <ErrorBox error={a.error} />
          </div>
        )}
        <ol className="timeline compact" style={{ marginTop: 12 }}>
          {[...r.data.events].reverse().map((e: any, i: number) => (
            <li key={i}>
              <span className="tl-dot" aria-hidden />
              <div>
                <strong>{{ entered: 'Entered', attempt: 'Attempt to serve', served: 'Served', note: 'Note', recall: 'Recalled', cancel: 'Cancelled', assign: 'Assigned', reactivate: 'Active again', updated: 'Entry corrected' }[e.action as string] ?? e.action}</strong>
                {e.location && <span className="small"> · {e.location}</span>}
                {e.note && <div className="small">{e.note}</div>}
                <div className="small muted">
                  {when(e.at)} · {e.by_name}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}

// ─── Shared form bits ─────────────────────────────────────────────────────────

function SubjectFields({ s, set, required = true }: { s: Subject; set: (s: Subject) => void; required?: boolean }) {
  const f = (k: string, label: string, extra: Record<string, unknown> = {}) => (
    <label>
      {label}
      <input value={s[k] ?? ''} onChange={(e) => set({ ...s, [k]: e.target.value })} {...extra} />
    </label>
  );
  return (
    <div className="stack sm">
      <div className="ls-row">
        <LicenseScanButton
          small
          onScan={(d) =>
            set({ ...s, last: d.last ?? '', first: d.first ?? '', middle: d.middle ?? '', suffix: d.suffix ?? '', dob: d.dob ?? '', sex: d.sex ?? '', height: d.height ?? s.height, weight: d.weight ? String(d.weight) : s.weight, hair: d.hair ?? s.hair, eyes: d.eyes ?? s.eyes, address: d.street ?? '', city: d.city ?? '', state: d.state ?? 'SC', zip: d.zip ?? '', dl: d.dl ?? '', dlState: d.dlState ?? 'SC' })
          }
        />
      </div>
      <div className="grid-4">
        {f('last', `Last name${required ? '' : ' (if known)'}`)}
        {f('first', 'First name')}
        {f('middle', 'Middle')}
        {f('suffix', 'Suffix', { placeholder: 'Jr.' })}
        {f('dob', 'Date of birth', { type: 'date' })}
        <label>
          Sex
          <select value={s.sex ?? ''} onChange={(e) => set({ ...s, sex: e.target.value })}>
            <option value="">—</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="U">Unknown</option>
          </select>
        </label>
        <label>
          Race
          <select value={s.race ?? ''} onChange={(e) => set({ ...s, race: e.target.value })}>
            <option value="">—</option>
            <option value="W">White</option>
            <option value="B">Black</option>
            <option value="A">Asian</option>
            <option value="I">American Indian / Alaska Native</option>
            <option value="P">Native Hawaiian / Pacific Islander</option>
            <option value="U">Unknown</option>
          </select>
        </label>
        {f('height', 'Height', { placeholder: '5-10' })}
        {f('weight', 'Weight')}
        {f('hair', 'Hair')}
        {f('eyes', 'Eyes')}
        {f('aliases', 'Aliases')}
      </div>
      <div className="grid-4">
        <label className="span-2">
          Address
          <input value={s.address ?? ''} onChange={(e) => set({ ...s, address: e.target.value })} />
        </label>
        {f('city', 'City')}
        <label>
          State / ZIP
          <span className="row gap-sm">
            <select value={s.state ?? 'SC'} onChange={(e) => set({ ...s, state: e.target.value })} aria-label="State">
              {US_STATES.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
            <input value={s.zip ?? ''} onChange={(e) => set({ ...s, zip: e.target.value })} aria-label="ZIP" />
          </span>
        </label>
        {f('dl', 'Driver’s license #')}
        <label>
          License state
          <select value={s.dlState ?? 'SC'} onChange={(e) => set({ ...s, dlState: e.target.value })}>
            {US_STATES.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label className="span-2">
          Other identifiers (scars, tattoos, vehicle…)
          <input value={s.description ?? ''} onChange={(e) => set({ ...s, description: e.target.value })} />
        </label>
      </div>
    </div>
  );
}

function ChargeRows({ list, set, withBond }: { list: Charge[]; set: (c: Charge[]) => void; withBond?: boolean }) {
  return (
    <div className="stack sm">
      {list.map((c, i) => (
        <div key={i} className="charge-row">
          <StatutePicker
            value={c.offense}
            label={`Charge ${i + 1}`}
            placeholder="Offense or statute (e.g. assault 3rd, 16-3-600)"
            onText={(v) => set(list.map((x, j) => (j === i ? { ...x, offense: v, cdr: null } : x)))}
            onPick={(p) => set(list.map((x, j) => (j === i ? { ...x, offense: chargeWords(p.description), statute: p.statute, cdr: p.cdr } : x)))}
          />
          <input value={c.statute} onChange={(e) => set(list.map((x, j) => (j === i ? { ...x, statute: e.target.value } : x)))} placeholder="Statute (16-3-600(E))" aria-label={`Statute ${i + 1}`} />
          <input type="number" min={1} max={99} value={c.counts} onChange={(e) => set(list.map((x, j) => (j === i ? { ...x, counts: Number(e.target.value) || 1 } : x)))} aria-label={`Counts ${i + 1}`} title="Counts" />
          {withBond && <input value={c.bond} onChange={(e) => set(list.map((x, j) => (j === i ? { ...x, bond: e.target.value } : x)))} placeholder="Bond" aria-label={`Bond ${i + 1}`} />}
          <button type="button" className="btn ghost sm" onClick={() => set(list.filter((_, j) => j !== i))} aria-label={`Remove charge ${i + 1}`}>
            ✕
          </button>
        </div>
      ))}
      <button type="button" className="btn sm add-charge" onClick={() => set([...list, { offense: '', statute: '', counts: 1, bond: '' }])}>
        + Add a charge
      </button>
    </div>
  );
}

// ─── Request a warrant ────────────────────────────────────────────────────────

export function WarrantRequestForm() {
  const { navigate } = useRouter();
  const [kind, setKind] = useState<WarrantKind>('arrest');
  const [caseNumber, setCaseNumber] = useState('');
  const [s, setS] = useState<Subject>(emptySubject);
  const [charges, setCharges] = useState<Charge[]>([{ offense: '', statute: '', counts: 1, bond: '' }]);
  const [pc, setPc] = useState('');
  const [place, setPlace] = useState('');
  const [items, setItems] = useState('');
  const [court, setCourt] = useState('');
  const [custody, setCustody] = useState(false);
  const [custodyWhere, setCustodyWhere] = useState('');
  const a = useAction();
  const search = kind === 'search';
  return (
    <div className="ops stack">
      <PageHeader crumb="Warrants" title="Request a warrant" sub="Goes to the warrants office, then the judge. Follow it on My warrants — you’ll get a message at each step." />
      <WarrantTabs />
      <Card title="What kind">
        <div className="seg-ctl wrap-seg" role="radiogroup" aria-label="Kind of warrant">
          {WARRANT_REQUEST_KINDS.map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>
              {WARRANT_KIND_LABEL[k]}
            </button>
          ))}
        </div>
        <div className="grid-3">
          <label>
            Case # (if there’s a report)
            <input value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} placeholder="2026-000123" />
          </label>
          <label>
            Court
            <input value={court} onChange={(e) => setCourt(e.target.value)} placeholder="Magistrate Court" />
          </label>
        </div>
      </Card>
      <Card title={search ? 'Person (if known)' : 'Who'}>
        <SubjectFields s={s} set={setS} required={!search} />
        {!search && (
          <div className={`wr-custody${custody ? ' on' : ''}`}>
            <label className="check">
              <input type="checkbox" checked={custody} onChange={(e) => setCustody(e.target.checked)} /> <strong>This person is in custody now</strong>
            </label>
            <span className="small">In-custody requests go to the top of the warrants office’s and the magistrate’s list, in red.</span>
            {custody && (
              <label>
                Where they’re held
                <input value={custodyWhere} onChange={(e) => setCustodyWhere(e.target.value)} placeholder="County detention center, booking" maxLength={200} />
              </label>
            )}
          </div>
        )}
      </Card>
      {search && (
        <Card title="The search">
          <label>
            Place, vehicle or thing to be searched
            <textarea rows={2} value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Single-wide mobile home, white with green trim, 418 Tanyard Rd, and outbuildings" />
          </label>
          <label>
            Items sought
            <textarea rows={2} value={items} onChange={(e) => setItems(e.target.value)} placeholder="Stolen copper wire, tools…" />
          </label>
        </Card>
      )}
      <Card title="Charges">
        <ChargeRows list={charges} set={setCharges} />
      </Card>
      <Card title="Probable cause">
        <textarea rows={8} value={pc} onChange={(e) => setPc(e.target.value)} placeholder="What happened, when and where, and the facts that give probable cause (witnesses, evidence, admissions)…" aria-label="Probable cause" />
        <span className="hint">{pc.trim().length < 40 ? 'Write out the facts — at least a few sentences.' : `${pc.trim().split(/\s+/).length} words`}</span>
      </Card>
      <div className="row gap-sm">
        <button
          className="btn primary"
          disabled={a.busy}
          onClick={() =>
            a.run(async () => {
              const r = await api.post<any>('/api/warrant-requests', {
                kind, caseNumber: caseNumber || null, subject: clean(s), courtName: court || null, probableCause: pc, searchPlace: place || null, itemsSought: items || null,
                inCustody: !search && custody, custodyWhere: !search && custody ? custodyWhere || null : null,
                charges: charges.filter((c) => c.offense.trim()).map((c) => clean(c)),
              });
              navigate(at(`/warrants/requests/${r.id}`), true);
            })
          }
        >
          Submit request
        </button>
      </div>
      <ErrorBox error={a.error} />
    </div>
  );
}

// ─── Requests: the warrants office and the judge ──────────────────────────────

export function WarrantQueue() {
  const { can } = useAuth();
  const { navigate } = useRouter();
  const box = can('warrants.enter') ? 'queue' : 'judge';
  const r = useLoad(() => api.get<any>(`/api/warrant-requests?box=${box}`), [box]);
  return (
    <div className="ops stack">
      <PageHeader crumb="Warrants" title={box === 'judge' ? 'Warrants to review' : 'Warrant requests'} sub={box === 'judge' ? 'Requests the warrants office sent to you.' : 'Deputies’ requests waiting on the warrants office or the judge, and issued warrants still to be entered.'} />
      <WarrantTabs />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.requests.length ? (
        <EmptyState icon="check" title="Nothing waiting" />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Kind</th>
                  <th>Subject</th>
                  <th>Charges</th>
                  <th>Deputy</th>
                  <th>Status</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {r.data.requests.map((x: any) => (
                  <tr key={x.id} className={`clickable${x.in_custody ? ' wr-custody-row' : ''}`} onClick={() => navigate(at(`/warrants/requests/${x.id}`))}>
                    <td className="mono">
                      {x.number}
                      {x.in_custody && <div className="wr-custody-tag">IN CUSTODY</div>}
                    </td>
                    <td>{WARRANT_KIND_LABEL[x.kind as WarrantKind]}</td>
                    <td>{x.kind === 'search' ? x.search_place : `${x.subject?.last ?? ''}, ${x.subject?.first ?? ''}`}</td>
                    <td className="wrap">{(x.charges ?? []).map((c: any) => c.offense).join('; ')}</td>
                    <td>{x.requested_by_name}</td>
                    <td>
                      <Badge tone={x.status === 'issued' && !x.warrant_id ? 'warn' : RTONE[x.status]}>{x.status === 'issued' && !x.warrant_id ? 'Issued — enter it' : WARRANT_REQUEST_STATUS_LABEL[x.status as WarrantRequestStatus]}</Badge>
                    </td>
                    <td>{when(x.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const STEPS: [string, WarrantRequestStatus[]][] = [
  ['Submitted', ['submitted', 'needs_info']],
  ['Warrants office', ['in_review']],
  ['Judge', ['sent_to_judge']],
  ['Issued', ['issued']],
];
function RequestSteps({ r }: { r: any }) {
  const cur = STEPS.findIndex(([, st]) => st.includes(r.status));
  const ended = r.status === 'denied' || r.status === 'withdrawn';
  return (
    <ol className="stages w-stages">
      {STEPS.map(([l], i) => (
        <li key={l} className={ended ? (i === 0 ? 'done' : '') : i < cur || (r.warrant_id && i === 3) ? 'done' : i === cur ? 'cur' : ''}>
          <span>{l}</span>
        </li>
      ))}
      <li className={r.warrant_id ? (r.warrant_status === 'active' ? 'cur' : 'done') : ended ? 'cur' : ''}>
        <span>{ended ? WARRANT_REQUEST_STATUS_LABEL[r.status as WarrantRequestStatus] : r.warrant_id ? (r.warrant_status === 'active' ? 'Active' : WARRANT_STATUS_LABEL[r.warrant_status as WarrantStatus]) : 'Active'}</span>
      </li>
    </ol>
  );
}

export function WarrantRequestDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/warrant-requests/${id}`), [id]);
  const [to, setTo] = useState<WarrantRequestStatus | null>(null);
  const [note, setNote] = useState('');
  const [judgeId, setJudgeId] = useState('');
  const [pc, setPc] = useState<string | null>(null);
  const a = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const x = r.data.request;
  const s = x.subject ?? {};
  const MOVE: Record<string, string> = { in_review: 'Start review', needs_info: 'Ask for more information', sent_to_judge: 'Send to the judge', issued: 'Issued (signed)', denied: 'Deny', withdrawn: 'Withdraw', submitted: 'Resubmit with the information' };
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<Link to={at(r.data.mine ? '/warrants/mine' : '/warrants/queue')}>Warrants</Link>}
        title={`Request ${x.number}`}
        sub={`${WARRANT_KIND_LABEL[x.kind as WarrantKind]} · requested by ${x.requested_by_name} ${when(x.created_at)}`}
        actions={
          r.data.canEnter && x.status === 'issued' && !x.warrant_id ? (
            <button className="btn primary" onClick={() => navigate(at(`/warrants/entry?request=${x.id}`))}>
              Enter the warrant
            </button>
          ) : x.warrant_id ? (
            <Link className="btn" to={at(`/warrants/${x.warrant_id}`)}>
              Warrant {x.warrant_number}
            </Link>
          ) : undefined
        }
      />
      {x.in_custody && (
        <div className="wr-custody-banner" role="alert">
          <strong>IN CUSTODY</strong> — this person is being held{x.custody_where ? ` at ${x.custody_where}` : ''}. Handle this request first.
        </div>
      )}
      <Card>
        <RequestSteps r={x} />
        <p>
          <Badge tone={RTONE[x.status]} dot>
            {WARRANT_REQUEST_STATUS_LABEL[x.status as WarrantRequestStatus]}
          </Badge>{' '}
          {x.judge_name && <span className="small muted">Judge: {x.judge_name}</span>} {x.handled_by_name && <span className="small muted">· Warrants office: {x.handled_by_name}</span>}
        </p>
        {r.data.moves.length > 0 && (
          <div className="stack sm">
            <div className="row gap-sm wrap">
              {r.data.moves.map((m: string) => (
                <button key={m} className={`btn ${to === m ? 'primary' : m === 'denied' || m === 'withdrawn' ? 'danger ghost' : ''}`} onClick={() => (setTo(m as WarrantRequestStatus), setNote(''), setPc(m === 'submitted' ? x.probable_cause : null))}>
                  {MOVE[m] ?? m}
                </button>
              ))}
            </div>
            {to && (
              <div className="stack sm">
                {to === 'sent_to_judge' && r.data.judges.length > 0 && (
                  <select value={judgeId} onChange={(e) => setJudgeId(e.target.value)} aria-label="Judge">
                    <option value="">Any judge on duty</option>
                    {r.data.judges.map((j: any) => (
                      <option key={j.id} value={j.id}>
                        {j.name}
                      </option>
                    ))}
                  </select>
                )}
                {pc !== null && (
                  <label>
                    Probable cause (add what was asked for)
                    <textarea rows={7} value={pc} onChange={(e) => setPc(e.target.value)} />
                  </label>
                )}
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={to === 'needs_info' ? 'What’s needed?' : to === 'denied' ? 'Why it’s denied' : 'Note (optional)'} aria-label="Note" />
                <div className="row gap-sm">
                  <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/warrant-requests/${id}/act`, { to, note: note || undefined, judgeId: judgeId || undefined, probableCause: pc ?? undefined }), setTo(null), r.reload()))}>
                    {MOVE[to]}
                  </button>
                  <button className="btn ghost" onClick={() => setTo(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <ErrorBox error={a.error} />
          </div>
        )}
      </Card>
      <div className="grid-2">
        <Card title={x.kind === 'search' ? 'The search' : 'Subject'}>
          <dl className="kv">
            {(s.last || s.first) && (
              <>
                <dt>Name</dt>
                <dd>
                  <strong>
                    {(s.last ?? '').toUpperCase()}, {[s.first, s.middle].filter(Boolean).join(' ')}
                  </strong>
                </dd>
              </>
            )}
            {s.dob && (
              <>
                <dt>DOB</dt>
                <dd>
                  {day(s.dob)} (age {age(s.dob)})
                </dd>
              </>
            )}
            {(s.address || s.city) && (
              <>
                <dt>Address</dt>
                <dd>{[s.address, s.city, s.state, s.zip].filter(Boolean).join(', ')}</dd>
              </>
            )}
            {x.search_place && (
              <>
                <dt>Place</dt>
                <dd className="pre">{x.search_place}</dd>
                <dt>Items sought</dt>
                <dd className="pre">{x.items_sought ?? '—'}</dd>
              </>
            )}
            <dt>Case #</dt>
            <dd>{x.incident_id ? <Link to={at(`/cases/${x.incident_id}`)}>{x.case_number}</Link> : x.case_number ?? '—'}</dd>
            <dt>Court</dt>
            <dd>{x.court_name ?? '—'}</dd>
          </dl>
        </Card>
        <Card title="Charges">
          {(x.charges ?? []).length ? (
            <ul className="plain">
              {x.charges.map((c: any, i: number) => (
                <li key={i}>
                  {c.offense} {c.statute && <span className="mono small muted">§ {c.statute}</span>} {c.counts > 1 && <span className="small">× {c.counts}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">None</p>
          )}
        </Card>
      </div>
      <Card title="Probable cause">
        <p className="pre">{x.probable_cause}</p>
      </Card>
      <Card title="History">
        <ol className="timeline compact">
          {r.data.events.map((e: any, i: number) => (
            <li key={i}>
              <span className="tl-dot" aria-hidden />
              <div>
                <strong>{e.action === 'entered' ? 'Warrant entered' : WARRANT_REQUEST_STATUS_LABEL[e.action as WarrantRequestStatus] ?? e.action}</strong>
                {e.note && <div className="small pre">{e.note}</div>}
                <div className="small muted">
                  {when(e.at)} · {e.by_name}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}

// ─── Entry (warrants office) ──────────────────────────────────────────────────

export function WarrantEntry() {
  const { path } = useRouter();
  const requestId = new URLSearchParams(path.split('?')[1] ?? '').get('request');
  const req = useLoad(() => (requestId ? api.get<any>(`/api/warrant-requests/${requestId}`) : Promise.resolve(null)), [requestId]);
  if (requestId && !req.data && !req.error) return <Spinner />;
  return <WarrantForm request={req.data?.request ?? null} />;
}

function WarrantForm({ request, existing, onDone }: { request?: any; existing?: any; onDone?: () => void }) {
  const { navigate } = useRouter();
  const src = existing ?? request ?? null;
  const staff = useLoad(() => api.get<any>('/api/mail/people').then((x) => x.people as any[]).catch(() => []), []);
  const [f, setF] = useState(() => ({
    warrantNumber: existing?.warrant_number ?? '',
    kind: (src?.kind ?? 'arrest') as WarrantKind,
    courtName: src?.court_name ?? '',
    judgeName: existing?.judge_name ?? request?.judge_name ?? '',
    issuedOn: existing?.issued_on ?? new Date().toLocaleDateString('en-CA'),
    caseNumber: src?.case_number ?? '',
    bond: existing?.bond ?? '',
    ncicNumber: existing?.ncic_number ?? '',
    notes: existing?.notes ?? '',
    assignedTo: existing?.assigned_to ?? request?.requested_by ?? '',
  }));
  const [s, setS] = useState<Subject>(() => ({ ...emptySubject(), ...Object.fromEntries(Object.entries(src?.subject ?? {}).map(([k, v]) => [k, v ?? ''])) }));
  const [charges, setCharges] = useState<Charge[]>(() => (src?.charges?.length ? src.charges.map((c: any) => ({ offense: c.offense ?? '', statute: c.statute ?? '', counts: c.counts ?? 1, bond: c.bond ?? '', cdr: c.cdr ?? null })) : [{ offense: '', statute: '', counts: 1, bond: '' }]));
  const [cautions, setCautions] = useState<string[]>(existing?.cautions ?? []);
  const a = useAction();
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const save = () =>
    a.run(async () => {
      const body = { ...clean(f), assignedTo: f.assignedTo || null, subject: clean(s), charges: charges.filter((c) => c.offense.trim()).map((c) => clean(c)), cautions, requestId: request?.id };
      if (existing) {
        const { assignedTo, requestId, ...rest } = body;
        await api.post(`/api/warrants/${existing.id}`, rest);
        onDone?.();
      } else {
        const r = await api.post<any>('/api/warrants', body);
        navigate(at(`/warrants/${r.id}`), true);
      }
    });
  return (
    <div className="ops stack">
      <PageHeader crumb="Warrants" title={existing ? `Correct warrant ${existing.warrant_number}` : 'Enter a warrant'} sub={request ? `From request ${request.number} by ${request.requested_by_name}` : 'Type in a warrant received from the court.'} />
      {!existing && <WarrantTabs />}
      <Card title="Warrant">
        <div className="grid-4">
          <label>
            Warrant #
            <input className="mono" value={f.warrantNumber} onChange={set('warrantNumber')} />
          </label>
          <label>
            Kind
            <select value={f.kind} onChange={set('kind')}>
              {WARRANT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {WARRANT_KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Issuing court
            <input value={f.courtName} onChange={set('courtName')} />
          </label>
          <label>
            Judge
            <input value={f.judgeName} onChange={set('judgeName')} />
          </label>
          <label>
            Issued
            <input type="date" value={f.issuedOn} onChange={set('issuedOn')} />
          </label>
          <label>
            Case #
            <input value={f.caseNumber} onChange={set('caseNumber')} />
          </label>
          <label>
            Bond
            <input value={f.bond} onChange={set('bond')} placeholder="$5,000 surety" />
          </label>
          <label>
            NCIC #
            <input className="mono" value={f.ncicNumber} onChange={set('ncicNumber')} />
          </label>
          {!existing && (
            <label>
              Assign to
              <select value={f.assignedTo} onChange={set('assignedTo')}>
                <option value="">Nobody yet</option>
                {(staff.data ?? []).map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="caution-boxes" role="group" aria-label="Caution">
          <span className="lbl">Caution</span>
          {WARRANT_CAUTIONS.map((c) => (
            <label key={c} className="check">
              <input type="checkbox" checked={cautions.includes(c)} onChange={(e) => setCautions(e.target.checked ? [...cautions, c] : cautions.filter((x) => x !== c))} /> {WARRANT_CAUTION_LABEL[c]}
            </label>
          ))}
        </div>
      </Card>
      <Card title="Subject">
        <SubjectFields s={s} set={setS} />
      </Card>
      <Card title="Charges">
        <ChargeRows list={charges} set={setCharges} withBond />
      </Card>
      <Card title="Notes">
        <textarea rows={3} value={f.notes} onChange={set('notes')} aria-label="Notes" />
      </Card>
      <div className="row gap-sm">
        <button className="btn primary" disabled={a.busy} onClick={save}>
          {existing ? 'Save corrections' : 'Enter warrant — make it active'}
        </button>
        {existing && (
          <button className="btn ghost" onClick={onDone}>
            Cancel
          </button>
        )}
      </div>
      <ErrorBox error={a.error} />
    </div>
  );
}

// ─── My warrants ──────────────────────────────────────────────────────────────

export function MyWarrants() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/warrants/mine'), []);
  return (
    <div className="ops stack">
      <PageHeader crumb="Warrants" title="My warrants" sub="Warrants you requested and where each one stands, and warrants assigned to you." />
      <WarrantTabs />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : (
        <>
          <Card title={`My requests (${r.data.requests.length})`}>
            {r.data.requests.length === 0 ? (
              <p className="muted">You haven’t requested any warrants.</p>
            ) : (
              <div className="case-list">
                {r.data.requests.map((x: any) => (
                  <button key={x.id} type="button" className="card case-card" onClick={() => navigate(at(`/warrants/requests/${x.id}`))}>
                    <div className="row">
                      <strong className="mono">{x.number}</strong>
                      <span>
                        {WARRANT_KIND_LABEL[x.kind as WarrantKind]} — {x.kind === 'search' ? x.search_place : `${(x.subject?.last ?? '').toUpperCase()}, ${x.subject?.first ?? ''}`}
                      </span>
                      <span className="spacer" />
                      {x.status === 'needs_info' && <Badge tone="warn" dot>They need more from you</Badge>}
                      {x.warrant_id && <Badge tone={x.warrant_status === 'active' ? 'bad' : 'ok'}>Warrant {x.warrant_number}: {WARRANT_STATUS_LABEL[x.warrant_status as WarrantStatus]}</Badge>}
                    </div>
                    <RequestSteps r={x} />
                    {x.last && (
                      <span className="small muted">
                        Latest: {x.last.action === 'entered' ? 'Warrant entered' : WARRANT_REQUEST_STATUS_LABEL[x.last.action as WarrantRequestStatus] ?? x.last.action}
                        {x.last.note ? ` — ${x.last.note}` : ''} · {x.last.by_name}, {when(x.last.at)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </Card>
          <Card title={`Warrants I’m working (${r.data.warrants.length})`}>
            {r.data.warrants.length === 0 ? (
              <p className="muted">None assigned to you.</p>
            ) : (
              <div className="w-list">
                {r.data.warrants.map((w: any) => (
                  <button key={w.id} type="button" className={`card w-row s-${w.status}`} onClick={() => navigate(at(`/warrants/${w.id}`))}>
                    <div className="w-main">
                      <strong className="w-name">{w.subject_name}</strong>
                      <span className="small">{(w.charges ?? []).map((c: any) => c.offense).join(' · ')}</span>
                      <span className="small muted">
                        {w.attempts} attempt{w.attempts === 1 ? '' : 's'} · {[w.subject?.address, w.subject?.city].filter(Boolean).join(', ')}
                      </span>
                      <Cautions list={w.cautions} />
                    </div>
                    <div className="w-side">
                      <Badge tone={WTONE[w.status]} dot>
                        {WARRANT_STATUS_LABEL[w.status as WarrantStatus]}
                      </Badge>
                      <span className="mono small">{w.warrant_number}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
